import { Link } from "react-router-dom";

import { usePortalStart, usePortalUebersicht } from "../../api/hooks";
import type { PortalAnlageEintrag } from "../../api/typen";
import { Hinweis, Karte, Spinner } from "../../komponenten/Basis";
import { AppHinweis } from "../../komponenten/portal/AppHinweis";
import { datum } from "../../lib/format";

function StatusPille({ eintrag }: { eintrag: PortalAnlageEintrag }) {
  const farbe = eintrag.aktion_noetig
    ? "bg-ampel-gelbHell text-ampel-gelb ring-ampel-gelb/30"
    : eintrag.status === "fertig"
      ? "bg-ampel-gruenHell text-ampel-gruen ring-ampel-gruen/30"
      : "bg-neutral-100 text-neutral-700 ring-neutral-300";
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${farbe}`}>
      {eintrag.aktion_noetig ? "Aktion nötig" : eintrag.status_text}
    </span>
  );
}

export function PortalStartSeite() {
  const start = usePortalStart();
  const uebersicht = usePortalUebersicht();

  if (start.isLoading || uebersicht.isLoading) return <div className="flex justify-center py-24"><Spinner label="Wird geladen…" /></div>;
  if (start.error || !start.data) return <Hinweis art="fehler">{start.error?.message ?? "Die Übersicht konnte nicht geladen werden."}</Hinweis>;
  if (uebersicht.error || !uebersicht.data) return <Hinweis art="fehler">{uebersicht.error?.message ?? "Die Anmeldungen konnten nicht geladen werden."}</Hinweis>;
  const { benutzer_name } = start.data;
  const { anlagen, aktion_noetig } = uebersicht.data;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Hallo {benutzer_name.split(" ")[0]}</h1>

      <Link
        to="/portal/neu"
        className="flex min-h-16 items-center justify-center rounded-2xl bg-marke px-6 py-4 text-lg font-semibold text-white shadow-karte hover:bg-marke-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2"
      >
        + Neue Anmeldung starten
      </Link>

      {aktion_noetig > 0 && (
        <Hinweis art="warnung">
          {aktion_noetig === 1 ? "Eine Anmeldung wartet" : `${aktion_noetig} Anmeldungen warten`} auf Ihre Unterlagen.
        </Hinweis>
      )}

      <AppHinweis />

      <section aria-labelledby="anmeldungen">
        <h2 id="anmeldungen" className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-500">Ihre Anmeldungen</h2>
        {anlagen.length === 0 ? (
          <Karte className="px-6 py-10 text-center">
            <p className="font-semibold">Noch keine Anmeldung</p>
            <p className="mt-1 text-sm text-neutral-600">Starten Sie oben Ihre erste. Es dauert nur wenige Minuten.</p>
          </Karte>
        ) : (
          <ul className="space-y-2">
            {anlagen.map((a) => (
              <li key={a.id}>
                <Link
                  to={`/portal/anmeldung/${a.id}`}
                  className="flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white px-4 py-4 shadow-karte hover:border-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold">{a.kunde_name}</p>
                    <p className="truncate text-sm text-neutral-500">
                      {a.ort ?? "Ort unbekannt"} · {a.dokumente_anzahl} {a.dokumente_anzahl === 1 ? "Datei" : "Dateien"}
                    </p>
                    <p className="text-xs text-neutral-400">{datum(a.aktualisiert_am)}</p>
                  </div>
                  <StatusPille eintrag={a} />
                  <span className="text-neutral-400" aria-hidden>›</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
